import { useCallback, useEffect, useRef, useState } from 'react';

import { setCookie } from 'utils/helpers/cookie';

import { DISMISS_COOKIE_PREFIX, DISMISS_MINUTES, PHASES } from './utils';

function getScrollPercent() {
  const { scrollHeight, clientHeight } = document.documentElement;
  const scrollable = scrollHeight - clientHeight;

  if (scrollable <= 0) return 0;

  return (window.scrollY / scrollable) * 100;
}

export default function useFloatingWidgetTrigger({
  id,
  enabled = true,
  appearanceScroll,
  appearanceSeconds,
  expandTimer,
}) {
  const [phase, setPhase] = useState(PHASES.hidden);
  const autoExpandCancelled = useRef(false);

  useEffect(() => {
    autoExpandCancelled.current = false;
    setPhase(PHASES.hidden);
  }, [id]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (phase !== PHASES.hidden) return undefined;

    const scrollThreshold = Number(appearanceScroll);
    const secondsThreshold = Number(appearanceSeconds);
    const hasScroll = Number.isFinite(scrollThreshold);
    const hasSeconds = Number.isFinite(secondsThreshold);

    if (!hasScroll && !hasSeconds) return undefined;

    let timer;

    const reveal = () => {
      setPhase(PHASES.collapsed);
    };

    const onScroll = () => {
      if (getScrollPercent() >= scrollThreshold) reveal();
    };

    if (hasScroll) {
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    if (hasSeconds) {
      timer = setTimeout(reveal, secondsThreshold * 1000);
    }

    return () => {
      if (hasScroll) window.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
    };
  }, [enabled, phase, appearanceScroll, appearanceSeconds]);

  useEffect(() => {
    if (phase !== PHASES.collapsed) return undefined;
    if (autoExpandCancelled.current) return undefined;

    const seconds = Number(expandTimer);
    if (!Number.isFinite(seconds)) return undefined;

    let timer;

    const restart = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setPhase(PHASES.expanded), seconds * 1000);
    };

    restart();
    window.addEventListener('scroll', restart, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', restart);
    };
  }, [phase, expandTimer]);

  const expand = useCallback(() => {
    autoExpandCancelled.current = true;
    setPhase(PHASES.expanded);
  }, []);

  const collapse = useCallback(() => {
    autoExpandCancelled.current = true;
    setPhase(PHASES.collapsed);
  }, []);

  const dismiss = useCallback(() => {
    autoExpandCancelled.current = true;
    setPhase(PHASES.dismissed);

    if (id) {
      setCookie(`${DISMISS_COOKIE_PREFIX}${id}`, '1', DISMISS_MINUTES);
    }
  }, [id]);

  return { phase, expand, collapse, dismiss };
}
